// What cruise draws into: the sim's mount in the page's one scene, its
// camera, the world frame and the bubble, and the bubble's lights. Built
// once per page, on the renderer and context-loss gate `createPageRenderer`
// already built - explore.ts calls that alone and needs no second copy of
// its pixel ratio, tone mapping, DOM-mount or context-loss steps here.
import * as THREE from 'three';
import type { ContextLossHandle } from '../gl/contextLoss';
import { worldScene } from '../world/scene';
import type { SubtreeHandle, WorldSceneHandle } from '../world/scene';
import type { WorldFrameHandle } from '../world/frame';
import { addLighting, type SimLighting } from '../scene/lighting';
import { createEnvMap } from '../scene/spaceEnv';
import { CRUISE_FOV_DEG } from '../sim/arrivalOrbit';

export interface CruiseStageDeps {
  renderer: THREE.WebGLRenderer;
  /** Built by `createPageRenderer` alongside the renderer; the stage takes
   *  it rather than building a second one, so play installs exactly one
   *  `webglcontextlost` listener. */
  contextLoss: ContextLossHandle;
}

export interface CruiseStage {
  renderer: THREE.WebGLRenderer;
  contextLoss: ContextLossHandle;
  world: WorldSceneHandle;
  scene: THREE.Scene;
  simWorld: SubtreeHandle;
  simRoot: THREE.Group;
  camera: THREE.PerspectiveCamera;
  worldFrame: WorldFrameHandle;
  bubble: THREE.Group;
  simLights: SimLighting;
  dispose(): void;
}

export function createCruiseStage(deps: CruiseStageDeps): CruiseStage {
  const { renderer, contextLoss } = deps;

  // ---------- Scene + camera ----------
  // One scene and one root for the whole page (`src/world/scene.ts`): the sim
  // draws its own subtree and the Atlas mounts another under the same root
  // instead of building a second scene per visit.
  const world = worldScene();
  const scene = world.scene;
  const simWorld = world.mount('sim');
  const simRoot = simWorld.group;
  const camera = new THREE.PerspectiveCamera(
    CRUISE_FOV_DEG,
    window.innerWidth / window.innerHeight,
    0.1,
    // The bodies are drawn at real heliocentric distances, so the far plane has
    // to reach past the outermost registry row from anywhere the ship can be.
    // Affordable because the renderer runs a logarithmic depth buffer.
    2e13,
  );
  camera.position.set(0, 6, 22);
  camera.lookAt(0, 0, 0);
  simWorld.camera = camera;
  // The sim's subtree is the one attached at boot; the Atlas takes the root when
  // it draws and hands it back on the next sim frame.
  simWorld.activate();

  // ---------- World frame ----------
  // The ship's own position is heliocentric doubles (`src/sim/position.ts`) and
  // everything the sim draws hangs off it: `ship.group` at `simPosition - anchor`
  // in the scene, and the whole fight inside `bubble` at `origin - anchor`. The
  // camera-relative pass takes the CAMERA's world position, the way
  // `src/atlas.ts` does, and the one node registered in the frame is where the
  // bubble's own origin sits in the world.
  const worldFrame = simWorld.frame;
  const bubble = new THREE.Group();
  bubble.name = 'combat-bubble';
  simRoot.add(bubble);

  // ---------- Lighting ----------
  // Off in cruise, where the Atlas's own sun is the light in the frame; on for
  // the fight, whose look was built around them.
  const simLights = addLighting(bubble);
  simLights.setFightLook(false);
  // IBL env map so PBR metal (the Custos hull) has something to reflect. The
  // existing addLighting() keeps owning the key/fill; this only adds reflections.
  // The key and fill hang off the bubble, not the subtree, so the fight is lit
  // the same way whatever orbital plane the bubble is tilted onto.
  // Scene-level state, so it rides on the subtree and is applied when the sim is
  // the side drawing: the Atlas's own materials are lit by its sun, not by this.
  const envMap = createEnvMap(renderer);
  simWorld.setSceneState({ environment: envMap, environmentIntensity: 0.45 });

  return {
    renderer, contextLoss, world, scene, simWorld, simRoot, camera, worldFrame, bubble, simLights,
    dispose: () => {
      contextLoss.dispose();
      simLights.dispose();
      envMap.dispose();
      simWorld.dispose();
    },
  };
}
